import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";

import { ReconciliationService } from "@/api";
import type { reconciliation_PaymentRegisterResponse } from "@/api";

/** Filters for the global cash-in register (issue #761, Phase 1b). */
export interface OrgPaymentRegisterFilter {
  /**
   * Which arm of the register to load (#1300). Absent is the pre-#1300
   * payments-only default; a caller must ask for "all" or "refund" to see the
   * refund arm. First member because it is the first positional argument of
   * the generated client method.
   */
  kind?: "payment" | "refund" | "all";
  status?: "unapplied" | "partially_applied" | "applied";
  debtorType?: "parent" | "office";
  from?: string;
  to?: string;
  importBatchId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Query-key factory for the global register. Scoped by orgId so a Clerk org
 * switch does not surface another tenant's cached register.
 */
export const orgPaymentRegisterKeys = {
  all: ["reconciliation", "payment-register"] as const,
  list: (
    orgId: string | null | undefined,
    filter: OrgPaymentRegisterFilter,
  ) => [...orgPaymentRegisterKeys.all, orgId ?? null, filter] as const,
};

/**
 * useOrgPaymentRegister loads one filtered page of the org-wide cash-in
 * register (every inbound payment, with the Debtor column populated). Keeps
 * the previous page visible while fetching the next.
 *
 * FRESHNESS POLICY (#1338) — the reasoning behind per-hook freshness lives in
 * the FRESHNESS POLICY block at the top of `useDebtorAccountsList.ts`; this
 * comment records only what THIS hook decided and why.
 *
 * Focus refetch is OFF, deliberately. This is the one money read in the set
 * that is both org-wide and unbounded by the operator's current selection: a
 * page costs List + Count + Sum, and the count/sum arms scan every non-deleted
 * `payments` row for the org plus the `manual_refund` arm of
 * `payment_applications`. That set grows every billing cycle, the Render
 * Postgres is Basic (0.5 CPU / 1 GB) and throttles, and nobody has measured
 * this endpoint at production scale. Adding a refetch trigger to an unmeasured
 * org-wide read is the same move #987's 60s staleTime revert declined to make
 * (`67238c419`, reasoning at `useDebtorAccountsList.ts:163-168`).
 *
 * Note this endpoint is NOT one of the two #1042 measures — that issue covers
 * the debtor-accounts full list path and the KPI summary. Turning this on
 * needs its own measurement, not #1042's result.
 *
 * The register is still not stranded: `invalidateMoneyViews` drops
 * `orgPaymentRegisterKeys.all` on every billing-domain write, in this tab and
 * (since #1037) across tabs. What stays uncovered is a change originating
 * outside any browser, and the manual refresh control answers that.
 *
 * staleTime is written out rather than inherited so a reader can tell this is
 * the chosen window and not an accident of the app default. The value is the
 * app default; it is unchanged by this issue.
 */
export function useOrgPaymentRegister(filter: OrgPaymentRegisterFilter = {}) {
  const { orgId } = useAuth();
  return useQuery<reconciliation_PaymentRegisterResponse>({
    queryKey: orgPaymentRegisterKeys.list(orgId, filter),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationPaymentsRegister(
        // kind (#1300): an absent value is still the payments-only pre-#1300
        // default, so a caller must pass "all" or "refund" to see the refund arm.
        filter.kind,
        filter.status,
        filter.debtorType,
        filter.from,
        filter.to,
        filter.importBatchId,
        filter.search,
        filter.limit,
        filter.offset,
      ),
    placeholderData: keepPreviousData,
  });
}
